/* eslint-disable jsx-a11y/no-autofocus */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button/standard-button";
import Modal, { ModalTransition } from "@atlaskit/modal-dialog";
import { token } from "@atlaskit/tokens";

import { CloseModalButton } from "@/components/product-demos/jsw-interactive-board/src/components/close-modal-button";
import { useLocalizedHref } from "@/libraries/web-framework";

import { messages } from "../messages";
import { Actions, completeDemo } from "../state";
import { useAppDispatch, useAppSelector } from "../state/hook";

export const CompleteDemoView = () => {
    const dispatch = useAppDispatch();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const ctaRef = useRef<HTMLAnchorElement>(null);
    const signupHref = useLocalizedHref("/try/cloud/signup");

    const [isOpen, setIsOpen] = useState(false);
    const openModal = useCallback(() => setIsOpen(true), []);
    const closeModal = useCallback(() => setIsOpen(false), []);
    const [state, isDemoComplete] = useAppSelector(
        ({ interactive_state, isDemoComplete }) => [
            interactive_state,
            isDemoComplete
        ]
    );
    const renderComponent = useMemo(
        () => state === Actions.COMPLETE_DEMO && !isDemoComplete,
        [state, isDemoComplete]
    );
    const ANIMATION_DELAY = 1500;

    useEffect(() => {
        if (renderComponent) {
            setTimeout(() => {
                openModal();

                createAnalyticsEvent({
                    action: "viewed",
                    actionSubject: "modal",
                    name: "interactiveRoadmapCelebrationModal",
                    eventType: "screen"
                }).fire();
            }, ANIMATION_DELAY);
        }
    }, [renderComponent]);

    const getItFreeClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "interactiveRoadmapGetItFree",
            eventType: "ui",
            attributes: {
                href: { signupHref },
                label: "Get it free"
            }
        }).fire();
    };

    const closeModalClick = () => {
        closeModal();
        dispatch(completeDemo());

        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "interactiveRoadmapModalCompleteClose",
            eventType: "ui"
        }).fire();

        document.getElementById("dependency-unlink-btn")?.focus();
    };

    if (!renderComponent) {
        return null;
    }

    return (
        <div data-testid="complete-demo-modal-container">
            <ModalTransition>
                {isOpen && (
                    <Modal
                        autoFocus={ctaRef}
                        onClose={closeModalClick}
                        width={440}
                        testId={"demo-complete-modal"}
                    >
                        <div css={ModalBackground}>
                            <div css={ModalHeader}>
                                <div css={HeaderLeft} />
                                <div
                                    css={css({
                                        ...HeaderCenter,
                                        backgroundImage:
                                            "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/confettiCelebration.webp)"
                                    })}
                                />
                                <div css={HeaderRight}>
                                    <CloseModalButton
                                        onCloseClick={closeModalClick}
                                    />
                                </div>
                            </div>
                            <div css={ModalTitle}>
                                <FormattedMessage
                                    {...messages.completeDemoModalHeader}
                                />
                            </div>
                            <div css={ModalSubheading}>
                                <FormattedMessage
                                    {...messages.completeDemoModalSubheader}
                                />
                            </div>
                            <Button
                                testId="get-it-free-btn"
                                css={GetFreeButton}
                                appearance="primary"
                                ref={ctaRef}
                                href={signupHref}
                                onClick={getItFreeClick}
                            >
                                <FormattedMessage
                                    {...messages.completeDemoModalButton}
                                />
                            </Button>
                        </div>
                    </Modal>
                )}
            </ModalTransition>
        </div>
    );
};

const ModalBackground = css({
    padding: "10px",
    width: "440px",
    height: "477px",
    borderRadius: "3px",
    paddingBlockStart: "5px",
    border: "var(--space-025, 2px) solid var(--Primary-Blue-700, #1868DB)",
    background: token("color.text.inverse", "#FFF"),
    boxShadow:
        "0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 8px 12px 0px rgba(9, 30, 66, 0.15)"
});

const ModalHeader = css({
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "flex-start",
    height: "auto",
    margin: "10px 0px"
});

const HeaderRight = css({
    width: "10%"
});
const HeaderLeft = css({
    width: "10%"
});

const HeaderCenter = {
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    height: "232px",
    width: "239px"
};

const ModalTitle = css({
    margin: "10px 15px",
    color: "#101214",
    fontFamily: "var(--bxp-charlie-text-font-family)",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "24px",
    letterSpacing: "0.3px"
});

const ModalSubheading = css({
    color: "#101214",
    fontFamily: "var(--bxp-charlie-text-font-family)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    margin: "10px 15px"
});

const GetFreeButton = css({
    margin: "10px 0px 24px 15px",
    fontSize: "14px"
});
